import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ACTIVE_SCREENS_QUERY_KEY } from "@/hooks/queries/useGetActiveScreens";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { NON_TERMINAL_INTERVIEW_STATUSES } from "@/lib/constants";
import { guacHttpClient } from "@/lib/http";
import {
  ActiveScreeningPrompt,
  DashboardInterview,
  HttpResponse,
  Interview,
  UpdateInterviewRequest,
} from "@/types";

// Updates the screen's interview from the global screening prompt. Ids are
// passed per-call since the popup is not scoped to the current route.
export function useUpdateScreeningPromptInterview() {
  const queryClient = useQueryClient();
  const successToast = useSuccessToast();

  return useMutation({
    mutationFn: async ({
      companyId,
      jobId,
      interviewId,
      updateInterviewRequest,
    }: {
      companyId: string;
      jobId: string;
      interviewId: number;
      updateInterviewRequest: UpdateInterviewRequest;
    }) => {
      const path = `companies/${companyId}/jobs/${jobId}/interviews/${interviewId.toString()}`;
      const res = await guacHttpClient.patch<
        Interview,
        HttpResponse<Interview>,
        UpdateInterviewRequest
      >(path, updateInterviewRequest);

      return res.data;
    },
    onSuccess: (updatedInterview, variables) => {
      const { companyId, jobId, interviewId, updateInterviewRequest } =
        variables;
      const { dateTime, status } = updateInterviewRequest;
      const interviewPatch = {
        ...(dateTime !== undefined && { dateTime }),
        ...(status !== undefined && { status }),
      };

      // Drop the acted screen from the active screens prompt list.
      queryClient.setQueryData(
        ACTIVE_SCREENS_QUERY_KEY,
        (
          cached?: ActiveScreeningPrompt[],
        ): ActiveScreeningPrompt[] | undefined =>
          cached?.filter((screen) => screen.interviewId !== interviewId),
      );

      // Calendar 7-day schedule (non-terminal only): reschedule keeps it live
      // (just moves it), a terminal status drops it off the schedule.
      const isStillLive =
        status === undefined ||
        NON_TERMINAL_INTERVIEW_STATUSES.includes(status);
      queryClient.setQueriesData<DashboardInterview[]>(
        { queryKey: ["interviews-calendar"] },
        (cached) => {
          if (!cached) return cached;
          if (!isStillLive) {
            return cached.filter((interview) => interview.id !== interviewId);
          }
          return cached.map((interview) =>
            interview.id === interviewId
              ? { ...interview, ...interviewPatch }
              : interview,
          );
        },
      );

      // Candidate sheet "Interviews" tab list (shows all statuses): update in place.
      queryClient.setQueriesData<Interview[]>(
        { queryKey: ["interviews", companyId, jobId] },
        (cached) =>
          cached?.map((interview) =>
            interview.id === interviewId
              ? { ...interview, ...interviewPatch }
              : interview,
          ),
      );

      // Rescheduling a screen moves the staffer's google calendar event, so just confirm it worked.
      if (updatedInterview.calendarEventUpdated) {
        successToast({
          description:
            "Calendar event successfully updated in your Google calendar!",
        });
      }
    },
  });
}
